import { notFound } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetAccountsDetailPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
  accountId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the account detail page.
 *
 * Fetches business detail, account detail, and accounts list in a single
 * HTTP round trip on client-side navigation.
 * If accountId is "new", accountDetail is returned as null.
 */
export const getAccountsDetailPageData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetAccountsDetailPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("accountsDetail.pageData", async () => {
      const [businessMod, accountMod] = await Promise.all([
        import("../business/business.server"),
        import("./account.server"),
      ]);
      const { getBusinessDetailForUser } = businessMod;
      const { getAccountForUser, listAccountsForUser } = accountMod;

      const isNew = data.accountId === "new";
      const [businessDetail, accountDetail, accountsList] = await Promise.all([
        getBusinessDetailForUser(context.user.id, data.businessId),
        isNew
          ? Promise.resolve(null)
          : getAccountForUser(context.user.id, data.businessId, data.accountId),
        listAccountsForUser(context.user.id, data.businessId, { limit: 200 }),
      ]);

      if (!accountDetail && !isNew) {
        // notFound() (not a bare Response) — thrown Responses can't be
        // serialized by the server RPC layer (Seroval crash → 500) and aren't
        // rejected properly on the client. notFound() renders the route's
        // notFoundComponent with a clean 404.
        throw notFound();
      }

      return { businessDetail, accountDetail, accountsList } as const;
    });
  });
